import { isUndefined, pickBy } from 'lodash';

import client from 'core/axios';
import handleError from 'core/axios/handleError';
import { handleJSONResponse } from 'core/axios/handleResponse';
import RequestMethod from 'core/axios/RequestMethod';

export const searchStudentPlans = ({
  params = {},
  cancelToken = null,
  version = 'v1',
} = {}) =>
  client
    .request({
      url: `${version}/cp-student-plans/student-plan/plans/`,
      method: RequestMethod.GET,
      params,
      cancelToken,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const updatePlanViewed = ({
  payload,
  studentPlanUUID,
  version = 'v3',
}) =>
  client
    .request({
      url: `${version}/cp-student-plans/student-plans/${studentPlanUUID}/`,
      method: RequestMethod.PATCH,
      data: payload,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const updateAlternateCourseList = ({
  payload,
  studentId,
  studentPlanUUID,
  version = 'v1',
}) =>
  client
    .request({
      url: `${version}/cp-student-plans/student-plan/${studentId}/alternate-courses/${studentPlanUUID}/`,
      method: RequestMethod.PATCH,
      data: payload,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getAlternateCourseList = ({
  studentId,
  studentPlanUUID,
  version = 'v1',
} = {}) =>
  client
    .request({
      url: `${version}/cp-student-plans/student-plan/${studentId}/alternate-courses/${studentPlanUUID}/`,
      method: RequestMethod.GET,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getDistrictSetting = ({ studentPlanUUID, version = 'v3' } = {}) =>
  client
    .request({
      url: `${version}/cp-student-plans/student-plans/${studentPlanUUID}/district-setting/`,
      method: RequestMethod.GET,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getStudentPlan = ({
  studentPlanUUID,
  version = 'v3',
  params = {},
}) =>
  client
    .request({
      url: `${version}/cp-student-plans/student-plans/${studentPlanUUID}/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getStudentPlanHistory = ({ studentId, version = 'v1' }) =>
  client
    .request({
      url: `${version}/cp-student-plans/student-plan/${studentId}/history/`,
      method: RequestMethod.GET,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getStudentPlanPlannedPathways = ({
  studentPlanUuid,
  version = 'v3',
}) =>
  client
    .request({
      url: `${version}/cp-student-plans/student-plans/${studentPlanUuid}/plan-programs/`,
      method: RequestMethod.GET,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const updateStudentPlanPlannedPathways = ({
  studentPlanUUID,
  programSlotId,
  payload,
  cancelToken = null,
  version = 'v3',
}) =>
  client
    .request({
      url: `${version}/cp-student-plans/student-plans/${studentPlanUUID}/plan-programs/${programSlotId}/`,
      method: RequestMethod.POST,
      data: payload,
      cancelToken,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getStudentPlanProgramSlots = ({
  studentPlanUuid,
  version = 'v3',
}) =>
  client
    .request({
      url: `${version}/cp-student-plans/student-plans/${studentPlanUuid}/program-slots/`,
      method: RequestMethod.GET,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getStudentPlanTLRMetaList = ({
  studentPlanUUID,
  version = 'v3',
  cancelToken = null,
}) =>
  client
    .request({
      url: `${version}/cp-student-plans/student-plans/${studentPlanUUID}/top-level-requirements-meta/`,
      method: RequestMethod.GET,
      cancelToken,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getStudentPlanTLRList = ({
  studentPlanUUID,
  cancelToken = null,
  version = 'v3',
}) =>
  client
    .request({
      url: `${version}/cp-student-plans/student-plans/${studentPlanUUID}/plan-top-level-requirements/`,
      cancelToken,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getPlanCourseList = ({
  studentPlanUUID,
  cancelToken = null,
  version = 'v3',
}) =>
  client
    .request({
      url: `${version}/cp-student-plans/student-plans/${studentPlanUUID}/plan-courses/`,
      cancelToken,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const sendPlanCoursesPlacement = ({
  studentPlanUUID,
  version = 'v3',
  payload,
}) =>
  client
    .request({
      url: `${version}/cp-student-plans/student-plans/${studentPlanUUID}/plan-courses-placement/`,
      method: RequestMethod.POST,
      data: payload,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getPlacementsMeta = ({
  studentPlanUUID,
  params = {},
  version = 'v3',
}) =>
  client
    .request({
      url: `${version}/cp-student-plans/student-plans/${studentPlanUUID}/plan-courses/placements-meta/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getCourseMetaList = ({
  studentPlanUUID,
  cancelToken = null,
  version = 'v3',
}) =>
  client
    .request({
      url: `${version}/cp-student-plans/student-plans/${studentPlanUUID}/courses-meta/`,
      cancelToken,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getSequenceListFromTLR = ({
  pickerConfigUUID,
  schoolIdList = '',
  studentPlanUUID = '',
  version = 'v1',
}) =>
  client
    .request({
      url: `${version}/cp-course-pickers/${pickerConfigUUID}/sequences/`,
      params: {
        schoolIdList,
        studentPlanUUID,
      },
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const addCourseToStudentPlan = ({
  studentPlanUUID,
  payload,
  cancelToken = null,
  version = 'v3',
}) =>
  client
    .request({
      url: `${version}/cp-student-plans/student-plans/${studentPlanUUID}/add-course/`,
      method: RequestMethod.POST,
      data: payload,
      cancelToken,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const removeCourseFromStudentPlan = ({
  studentPlanUUID,
  courseId,
  cancelToken = null,
  version = 'v3',
}) =>
  client
    .request({
      url: `${version}/cp-student-plans/student-plans/${studentPlanUUID}/remove-course/${courseId}`,
      method: RequestMethod.POST,
      cancelToken,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const addDirectAlternateToPlannedCourse = ({
  planCourseId,
  payload,
  version = 'v3',
}) =>
  client
    .request({
      url: `${version}/cp-student-plans/plan-courses/${planCourseId}/alternates/`,
      method: RequestMethod.POST,
      data: payload,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const removeDirectAlternateToPlannedCourse = ({
  planCourseId,
  courseId,
  version = 'v3',
}) =>
  client
    .request({
      url: `${version}/cp-student-plans/plan-courses/${planCourseId}/alternates/`,
      method: RequestMethod.DELETE,
      params: {
        courseId,
      },
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const updatePlanSequence = ({
  studentPlanUUID,
  planTopLevelRequirementId,
  sequenceId,
  version = 'v3',
}) =>
  client
    .request({
      url: `${version}/cp-student-plans/student-plans/${studentPlanUUID}/plan-top-level-requirement/${planTopLevelRequirementId}/update-sequence`,
      method: RequestMethod.POST,
      data: { sequence_id: sequenceId },
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const recalculatePlan = ({
  studentPlanUUID,
  version = 'v3',
  params = {},
  cancelToken = null,
}) =>
  client
    .request({
      url: `${version}/cp-student-plans/student-plans/${studentPlanUUID}/calculate-error/`,
      method: RequestMethod.POST,
      params,
      cancelToken,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const resetPlan = ({ studentId, version = 'v1' }) =>
  client
    .request({
      url: `${version}/cp-student-plans/student-plan${
        studentId ? `/${studentId}` : ''
      }/reset/`,
      method: RequestMethod.POST,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const submitPlan = ({
  studentId,
  note,
  submitAndApprove,
  unapprove,
  action,
  version = 'v1',
}) =>
  client
    .request({
      url: `${version}/cp-student-plans/student-plan/${
        studentId ? `${studentId}/` : ''
      }submit`,
      method: RequestMethod.PATCH,
      data: pickBy(
        { submit: true, note, submitAndApprove, unapprove, action },
        e => !isUndefined(e),
      ),
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getPlannedSchools = ({ studentPlanUUID, version = 'v3' }) =>
  client
    .request({
      url: `${version}/cp-student-plans/student-plans/${studentPlanUUID}/plan-schools`,
      method: RequestMethod.GET,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getGraduationPlans = ({ studentPlanUUID, version = 'v3' }) =>
  client
    .request({
      url: `${version}/cp-student-plans/student-plans/${studentPlanUUID}/graduation-plans`,
      method: RequestMethod.GET,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const saveStudentConfig = ({
  studentPlanUUID,
  property,
  entityId,
  version = 'v3',
}) =>
  client
    .request({
      url: `${version}/cp-student-plans/student-plans/${studentPlanUUID}/update-student-config`,
      method: RequestMethod.POST,
      data: { [property]: entityId },
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getCourse = ({ studentPlanUUID, courseId, version = 'v3' }) =>
  client
    .request({
      url: `${version}/cp-student-plans/student-plans/${studentPlanUUID}/course-details/${courseId}`,
      method: RequestMethod.GET,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getNonPlannedCourse = ({
  studentPlanUUID,
  courseId,
  version = 'v3',
}) =>
  client
    .request({
      url: `${version}/cp-student-plans/student-plans/${studentPlanUUID}/non-selected-course/${courseId}`,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getAdditionalCourses = ({ studentPlanUUID, version = 'v3' }) =>
  client
    .request({
      url: `${version}/cp-student-plans/student-plans/${studentPlanUUID}/plan-top-level-requirement/additional-courses/`,
      method: RequestMethod.GET,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const removeSequenceSelection = ({
  sequenceId,
  studentPlanUUID,
  planTopLevelRequirementId,
  version = 'v3',
}) =>
  client
    .request({
      url: `${version}/cp-student-plans/student-plans/${studentPlanUUID}/plan-top-level-requirement/${planTopLevelRequirementId}/remove-sequence`,
      method: RequestMethod.POST,
      data: { sequenceId },
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const duplicateSequence = ({
  coursePickerUUID,
  sequenceId,
  version = 'v1',
}) =>
  client
    .request({
      url: `${version}/cp-course-pickers/${coursePickerUUID}/sequences/${sequenceId}/duplicate/`,
      method: RequestMethod.POST,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getSequenceCourses = ({
  coursePickerUUID,
  sequenceId,
  version = 'v1',
}) =>
  client
    .request({
      url: `${version}/cp-course-pickers/${coursePickerUUID}/sequences/${sequenceId}/sequence-courses/`,
      method: RequestMethod.GET,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getPlanListContext = ({ version = 'v1' } = {}) =>
  client
    .request({
      url: `${version}/cp-student-plans/student-plan/plan-list-context/`,
      method: RequestMethod.GET,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getCoursePlanSummaryReport = ({
  params = {},
  payload = {},
  version = 'v1',
}) =>
  client
    .request({
      url: `${version}/cp-student-plans/course-plan-summary-csv/`,
      method: RequestMethod.POST,
      data: payload,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getPlannedCoursesReport = ({
  params = {},
  payload = {},
  version = 'v1',
}) =>
  client
    .request({
      url: `${version}/cp-student-plans/course-request-csv/`,
      method: RequestMethod.POST,
      data: payload,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getCoursePlanStudentCountReport = ({
  params = {},
  payload = {},
  version = 'v1',
}) =>
  client
    .request({
      url: `${version}/cp-student-plans/student-count-planned-courses-csv/`,
      method: RequestMethod.POST,
      data: payload,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getCourseRequestCsvSisReport = ({
  params = {},
  payload = {},
  version = 'v1',
}) =>
  client
    .request({
      url: `${version}/cp-student-plans/course-request-csv-sis/`,
      method: RequestMethod.POST,
      data: payload,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getStudentSelectedProgramCsvReport = ({
  payload = {},
  version = 'v1',
}) =>
  client
    .request({
      url: `${version}/cp-student-plans/student-selected-programs-csv/`,
      method: RequestMethod.POST,
      data: payload,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getCoursePlansReport = ({
  params = {},
  payload = {},
  version = 'v1',
}) =>
  client
    .request({
      url: `${version}/cp-student-plans/student-plan/report/`,
      method: RequestMethod.POST,
      data: payload,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getCoursePlansRequirementCreditCheckReport = ({
  params = {},
  payload = {},
  version = 'v1',
}) =>
  client
    .request({
      url: `${version}/cp-student-plans/requirement-credit-check-csv/`,
      method: RequestMethod.POST,
      data: payload,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getAlternateCourseReport = ({
  params = {},
  payload = {},
  version = 'v1',
}) =>
  client
    .request({
      url: `${version}/cp-student-plans/alternate-course-requests-csv/`,
      method: RequestMethod.POST,
      data: payload,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const removeInvalidCourses = ({ version = 'v3', studentPlanUUID }) =>
  client
    .request({
      url: `${version}/cp-student-plans/student-plans/${studentPlanUUID}/remove-invalid-courses/`,
      method: RequestMethod.POST,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getCourseUsageReport = ({
  params = {},
  payload = {},
  version = 'v1',
}) =>
  client
    .request({
      url: `${version}/cp-student-plans/course-usage-csv/`,
      method: RequestMethod.POST,
      data: payload,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getPlanNavigation = ({
  params = {},
  cancelToken = null,
  version = 'v1',
} = {}) =>
  client
    .request({
      url: `${version}/cp-student-plans/plan-navigation/`,
      method: RequestMethod.GET,
      params,
      cancelToken,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getTopLevelRequirementProgressStatus = ({
  topLevelRequirementId,
  studentPlanUUID,
  version = 'v3',
}) =>
  client
    .request({
      url: `${version}/cp-student-plans/student-plans/${studentPlanUUID}/top-level-requirement/${topLevelRequirementId}/progress-status/`,
      method: RequestMethod.GET,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getStudentPlanConfig = ({
  studentPlanUUID,
  version = 'v3',
} = {}) =>
  client
    .request({
      url: `${version}/cp-student-plans/student-plans/${studentPlanUUID}/student-config/`,
      method: RequestMethod.GET,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getCreditPlacementReason = ({ studentPlanUUID, version = 'v3' }) =>
  client
    .request({
      url: `${version}/cp-student-plans/student-plans/${studentPlanUUID}/course-placement-credit-count-reason/`,
      method: RequestMethod.GET,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getOtherTLRPlacementReason = ({
  studentPlanUUID,
  version = 'v3',
}) =>
  client
    .request({
      url: `${version}/cp-student-plans/student-plans/${studentPlanUUID}/course-other-tlr-placement-reason/`,
      method: RequestMethod.GET,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const downloadSupplementalPlanPDF = ({ params, version = 'v3' }) =>
  client
    .request({
      url: `${version}/cp-student-plans/get-graduation-plan-pdf/`,
      method: RequestMethod.POST,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);
